import type {
  AccountFormValues,
  AccountStatus,
  AccountUser,
  AiProvider,
  ApplicationRow,
  CommunicationType,
  IpOctets,
  LoggingStatus,
} from './types/account';

export const MAX_ACCOUNT_NAME_LENGTH = 150;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/;

export const ACCOUNT_STATUS_CHOICES: Array<{ id: AccountStatus; name: string }> = [
  { id: 'active', name: 'resources.accounts.status.active' },
  { id: 'limited', name: 'resources.accounts.status.limited' },
  { id: 'blocked', name: 'resources.accounts.status.blocked' },
];

export const AI_PROVIDER_CHOICES: Array<{ id: AiProvider; name: string }> = [
  { id: 'gemini', name: 'Google Gemini' },
  { id: 'groq', name: 'Groq' },
  { id: 'openrouter', name: 'OpenRouter' },
];

export const LOGGING_STATUS_CHOICES: Array<{ id: LoggingStatus; name: string }> = [
  { id: 'none', name: 'resources.accounts.logging.none' },
  { id: 'wrong', name: 'resources.accounts.logging.wrong' },
  { id: 'all', name: 'resources.accounts.logging.all' },
];

export const COMMUNICATION_TYPE_CHOICES: Array<{ id: CommunicationType; name: string }> = [
  { id: 'email', name: 'resources.accounts.channels.email' },
  { id: 'sms', name: 'resources.accounts.channels.sms' },
  { id: 'whatsapp', name: 'resources.accounts.channels.whatsapp' },
  { id: 'telegram', name: 'resources.accounts.channels.telegram' },
  { id: 'push', name: 'resources.accounts.channels.push' },
];

export const PUSH_DEVICE_TYPE_CHOICES = [
  { id: 'MOBILE', name: 'Mobile' },
  { id: 'WEB', name: 'Web' },
] as const;

export const PUSH_PLATFORM_CHOICES_MOBILE = [
  { id: 'ANDROID', name: 'Android' },
  { id: 'IOS', name: 'iOS' },
] as const;

export const PUSH_PLATFORM_CHOICES_WEB = [{ id: 'WEB', name: 'Web' }] as const;

export const EMPTY_IP_OCTETS: IpOctets = {
  octet1: '',
  octet2: '',
  octet3: '',
  octet4: '',
};

export const createEmptyUser = (): AccountUser => ({
  name: '',
  surname: '',
  email: '',
  password: '',
  confirmPassword: '',
  // Arranca con lo minimo util: dar permisos de mas por defecto es como se colo
  // «gestionar usuarios» a todo el que se daba de alta.
  scopes: ['portal'],
});

export const createEmptyApplicationRow = (): ApplicationRow => ({
  notify: false,
  templatify: false,
  applicationId: '',
  name: '',
  tag: '',
  options: [],
  status: '',
  isApplicationIntegration: false,
});

export const generateApiKey = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const createEmptyAccountFormValues = (): AccountFormValues => ({
  accountName: '',
  directoryId: null,
  apiKey: generateApiKey(),
  accountStatus: 'active',
  loggingStatus: 'none',
  logRetentionDays: null,
  licenseId: '',
  validityTimeFrame: null,
  providerIds: [],
  communicationTypes: [],

  isLicenseBlocked: false,

  rateLimitApplies: false,
  rateLimitPerMin: 5,
  rateLimitPerDay: 5,

  whitelistApplies: false,
  whiteListedIps: [{ ...EMPTY_IP_OCTETS }],

  // No Add-Users toggle anymore — the users list itself drives the flag
  // (derived on save: addUser = users.length > 0).
  addUser: false,
  users: [],

  useCostCenter: false,
  contactCenterEnabled: false,
  // Portal y automatizaciones se dan por contratados al dar de alta: es lo que
  // tienen hoy todas las cuentas, y quitarlo es la excepcion, no la norma.
  portalEnabled: true,
  automationsEnabled: true,
  maxUsers: '',
  contactCenterReplyToEmail: '',
  costCenterId: null,

  useTemplates: false,

  aiAvailable: false,
  aiProvider: '',
  aiApiKey: '',
  aiApiKeyConfigured: false,
  aiTranscriptionApiKey: '',

  useTemplatify: false,
  autoCreateEnabled: true,
  autoCreateGlobal: false,
  templatifyGroupId: null,
  templatifyExtraApplicationIds: [],
  accountMode: '',
  rmkTemplateAccountName: '',
  rmkTemplateApiKey: '',
  rmkExistTemplateAccount: null,
  rmkExistTemplateApiKey: '',
  isUpdateTemplateApiKey: false,

  applications: [createEmptyApplicationRow()],
  applicationGroups: [],
});
